import { RECONITION_GOAL_QK } from '@components/RecognitionCounter'
import {
  CreateRecognitionParams,
  ErrorState,
  PointsExceptionError,
  RecognitionStatus,
  createDelegationRecognition,
  createRecognition,
  editRecognition,
} from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import { allPointsQueryKey } from '@shared/hooks/useMemberPoints'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { useMutation } from '@tanstack/react-query'
import { MANAGER_SENT_RECOGNITION_QK } from '@tenant/home/manager/useManagerRecognitionSummary'
import { TIMELINE_ITEMS_QK } from '@tenant/home/user/timeline/useTimelineItems'
import { MY_TIMELINE_ITEMS_QK } from '@tenant/me/myTimeline/useMyTimelineItems'
import { MEMBER_TIMELINE_ITEMS_QK } from '@tenant/memberProfile/useMemberTimelineItems'
import useRecognitionModal, {
  RecognitionModalViews,
} from '@tenant/recognition/RecognitionModal/useRecognitionModal'
import { RECOGNITION_COUNT_QUERY_KEY } from '@tenant/recognition/useConfigureRecognitionTabs'
import { AxiosError } from 'axios'
import { isFinite } from 'lodash/fp'
import { UseFormSetError } from 'react-hook-form'
import { URLSearchParamsInit, useSearchParams } from 'react-router'
import { useRecognitionRefreshStore } from '../../../yourDirectReports/recognitionRefreshStore'
import { RecognitionFormData } from './useSendRecognitionForm'

const useSendRecognitionMutations = (
  setIsShowAnimation: (showAnimation: boolean) => void,
  setPointsException: (pointsExceptions?: PointsExceptionError) => void,
  setError: UseFormSetError<RecognitionFormData>,
  recognitionId?: string,
) => {
  const [_searchParams, setSearchParams] = useSearchParams()
  const invalidateQuery = useInvalidateQuery()
  const { handleApiError } = useApiError<RecognitionFormData>()
  const { setView, dialogData } = useRecognitionModal()
  const delegatorId = dialogData?.delegatorId
  const triggerRecognitionRefresh = useRecognitionRefreshStore(state => state.triggerRefresh)

  const createParams = (
    data: RecognitionFormData,
    status: RecognitionStatus,
    ignoreExceptions: boolean = false,
  ) => {
    const params: CreateRecognitionParams = {
      message: data.message,
      amount: Number(data.amount),
      attachments: (data.attachments ?? []).map(x => x.mediaObjectId),
      qualityIds: data.qualities.map(quality => quality.id),
      sendTo: getSendToFromTenantMemberAndTeams(data.sendTo),
      gifs: !!data.gif ? [data.gif] : [],
      scheduledAt: data.scheduledAt,
      status,
      ignoreExceptions,
      tenantWide: data.tenantWide,
      refinementId: data.refinementId,
    }

    return params
  }

  const setErrors = (error: Error) => {
    handleApiError(error, setError, ['message', 'qualities', 'amount', 'sendTo'])
  }

  type MutationVariables = {
    params: CreateRecognitionParams
    editRecognitionId?: string
  }

  const { mutateAsync: createOrSaveRecognitionMutation, isPending: isPendingCreateOrSave } =
    useMutation({
      mutationFn: ({ params, editRecognitionId }: MutationVariables) => {
        if (delegatorId) {
          return createDelegationRecognition(delegatorId, params)
        }
        const idToUse = editRecognitionId ?? recognitionId
        return idToUse ? editRecognition(idToUse, params) : createRecognition(params)
      },
    })

  const publishRecognition = (
    data: RecognitionFormData,
    ignoreExceptions: boolean = false,
    resetRecognitionForm: () => void,
  ) => {
    return createOrSaveRecognitionMutation(
      {
        params: createParams(data, RecognitionStatus.PUBLISHED, ignoreExceptions),
        editRecognitionId: recognitionId,
      },
      {
        onSuccess: async (_data, variables) => {
          setIsShowAnimation(true)
          if (delegatorId) {
            await invalidateQuery(['delegatorPoints', delegatorId])
          } else {
            await invalidateQuery([allPointsQueryKey])
          }
          await invalidateQuery([TIMELINE_ITEMS_QK])
          await invalidateQuery([MY_TIMELINE_ITEMS_QK])
          await invalidateQuery([RECONITION_GOAL_QK])
          await invalidateQuery([MEMBER_TIMELINE_ITEMS_QK])
          await invalidateQuery([MANAGER_SENT_RECOGNITION_QK])
          if (variables.editRecognitionId) {
            await invalidateQuery(RECOGNITION_COUNT_QUERY_KEY)
          }
          resetRecognitionForm()
          setPointsException()
          triggerRecognitionRefresh()
        },
        onError: (error: Error) => {
          const errorType = (error as AxiosError<ErrorState>)?.response?.data?.error

          if (errorType === 'ValidationError') {
            setErrors(error)
            return
          }
          if (errorType === 'PointsExceptionError') {
            setSearchParams({ pointsException: 'true' } as URLSearchParamsInit)
            setPointsException(
              (error as AxiosError<ErrorState>)?.response?.data as unknown as PointsExceptionError,
            )
            return
          }
          handleApiError(error)
        },
      },
    )
  }

  const saveDraftRecognition = async (
    data: RecognitionFormData,
    recognitionIdOverride?: string,
  ) => {
    const editRecognitionId = recognitionIdOverride ?? recognitionId
    await createOrSaveRecognitionMutation(
      {
        params: createParams(
          {
            ...data,
            amount: isFinite(data.amount) ? data.amount : 0,
          },
          RecognitionStatus.DRAFT,
        ),
        editRecognitionId,
      },
      {
        onSuccess: () => {
          invalidateQuery(RECOGNITION_COUNT_QUERY_KEY)
          setView({
            view: RecognitionModalViews.DraftAndInProgressRecognitions,
          })
        },
        onError: error => setErrors(error),
      },
    )
  }

  return {
    publishRecognition,
    saveDraftRecognition,
    isPendingCreateOrSave,
  }
}

export default useSendRecognitionMutations
